import type { SortDirection } from "#app/lib/data";
import { UNASSIGNED_GROUP_KEY } from "./issue-grouping";

// Pure column-builder for the workflow board: turns a grouping dimension + its
// source groups + the server group counts into the ordered list of Kanban
// columns. React/Apollo-free so it unit-tests in the node runner; `board.tsx`
// owns data fetching and header rendering.

/**
 * Board grouping dimensions (a subset of `IssueGroupDimension` — no issueType).
 * `teams` is the DIRECT Teams property — the picker's team dimension. `team`
 * (assignee-membership expansion) stays filter-only: it double-counts an issue
 * whose assignees span teams.
 */
export const BOARD_GROUP_DIMENSIONS = [
  "stage",
  "workflow",
  "assignee",
  "team",
  "teams",
  "age",
] as const;

export type BoardGroupDimension = (typeof BOARD_GROUP_DIMENSIONS)[number];

/**
 * Narrow a stored grouping key. A saved view carries `config.groupBy` as a
 * free-form string — it may name a dimension a later build added, or one this
 * scope does not offer — so the board falls back rather than rendering a
 * board with no columns.
 */
export function toBoardGroupDimension(
  value: string,
  fallback: BoardGroupDimension,
): BoardGroupDimension {
  return (BOARD_GROUP_DIMENSIONS as readonly string[]).includes(value)
    ? (value as BoardGroupDimension)
    : fallback;
}

/** A candidate group for a dimension, carrying its source payload for headers. */
export interface BoardColumnSource<T> {
  key: string;
  label: string;
  data: T;
}

/** A resolved board column. `data` is null only for the synthetic unassigned bucket. */
export interface BuiltBoardColumn<T> {
  /** Globally-unique column id: `${dimension}:${key}`. */
  id: string;
  key: string;
  label: string;
  dimension: BoardGroupDimension;
  isUnassigned: boolean;
  data: T | null;
}

/**
 * Whether a dimension shows EVERY source column (true) or only the non-empty
 * ones the count query returned (false). Stage, workflow and age are bounded,
 * ordered sets — always show them all (workflow has no count query, so its
 * columns rely on each column's own `total`). Assignee and team can have
 * hundreds of members, so we only render columns that actually hold issues
 * (+ an unassigned bucket when the count query reports one).
 */
function showsAllColumns(dimension: BoardGroupDimension): boolean {
  return (
    dimension === "stage" || dimension === "workflow" || dimension === "age"
  );
}

/**
 * Build the ordered board columns for a dimension.
 *
 * - `stage` / `age`: every source in its given order (preserveOrder — stage
 *   order and age severity are meaningful), `sortDirection` ignored and
 *   `counts` unread — an empty bucket still renders (the board collapses it
 *   when the view asks, it never drops it).
 * - `assignee` / `team`: only sources whose `counts[key] > 0`, sorted by label
 *   (`sortDirection`), then an unassigned column appended when
 *   `counts[UNASSIGNED_GROUP_KEY] > 0`.
 */
export function buildBoardColumns<T>(opts: {
  dimension: BoardGroupDimension;
  sources: ReadonlyArray<BoardColumnSource<T>>;
  counts: Record<string, number>;
  sortDirection: SortDirection;
  /** Label for the synthetic unassigned column (e.g. "No assignee"). */
  unassignedLabel: string;
}): BuiltBoardColumn<T>[] {
  const { dimension, sources, counts, sortDirection, unassignedLabel } = opts;

  const toColumn = (
    key: string,
    label: string,
    data: T | null,
    isUnassigned: boolean,
  ): BuiltBoardColumn<T> => ({
    id: `${dimension}:${key}`,
    key,
    label,
    dimension,
    isUnassigned,
    data,
  });

  if (showsAllColumns(dimension)) {
    // Sources already include their own unassigned entry (age) or have none
    // (stage). Preserve the given order.
    return sources.map((s) =>
      toColumn(s.key, s.label, s.data, s.key === UNASSIGNED_GROUP_KEY),
    );
  }

  // assignee / team: non-empty sources, sorted by label.
  const dir = sortDirection === "desc" ? -1 : 1;
  const nonEmpty = sources
    .filter((s) => (counts[s.key] ?? 0) > 0)
    .sort((a, b) => a.label.localeCompare(b.label) * dir)
    .map((s) => toColumn(s.key, s.label, s.data, false));

  if ((counts[UNASSIGNED_GROUP_KEY] ?? 0) > 0) {
    nonEmpty.push(
      toColumn(UNASSIGNED_GROUP_KEY, unassignedLabel, null, true),
    );
  }

  return nonEmpty;
}
